import React, { useRef, useState, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import { ChevronLeft, Camera } from "lucide-react";

// ?rotate=true rotates the camera 90° (camera mounted sideways, like the
// Union photobooth). ?res=4k|1080|720 overrides the capture resolution.
const urlParams = new URLSearchParams(window.location.search);
const cameraRotated = urlParams.get("rotate") === "true";
const RES_PRESETS = { "4k": [3840, 2160], 1080: [1920, 1080], 720: [1280, 720] };
const camRes = RES_PRESETS[urlParams.get("res")] || RES_PRESETS["4k"];

// Landscape 4K sensor; cap with `max` so the camera can't overshoot and starve
// the USB bus (same approach as the Union booth).
const videoConstraints = {
  width: { ideal: camRes[0], max: camRes[0] },
  height: { ideal: camRes[1], max: camRes[1] },
  frameRate: { ideal: 30, max: 30 },
  facingMode: "user",
};

// Rotated preview: the container is 3:4 portrait; the video element is laid
// out as a box whose rotated footprint exactly covers the frame (width =
// container height = 133.33% of width; height = container width = 75% of
// height), then mirrored and rotated -90°.
const rotatedVideoStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "133.33%",
  height: "75%",
  // Tailwind preflight sets `video { max-width: 100% }`, which would clamp the
  // 133.33% width and collapse the rotated preview.
  maxWidth: "none",
  // -90°: the camera is now mounted on the opposite side of the screen.
  transform: "translate(-50%, -50%) scaleX(-1) rotate(-90deg)",
  objectFit: "cover",
};

export default function CaptureScreen({ onCaptured, onBack }) {
  const webcamRef = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const doCapture = useCallback(() => {
    const video = webcamRef.current && webcamRef.current.video;
    if (!video || !video.videoWidth) {
      setError("Posnetek ni uspel. Poskusi znova.");
      return;
    }
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const canvas = document.createElement("canvas");
    let ctx;

    // Photos are printed in 3:4 portrait, so the capture is cropped to 3:4.
    if (cameraRotated) {
      // Rotated sensor → portrait, center-cropped: 3840×2160 → 2160×2880.
      canvas.width = vh;
      canvas.height = Math.round((vh * 4) / 3);
      ctx = canvas.getContext("2d");
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.scale(-1, 1);
      ctx.rotate(-Math.PI / 2);
      // Draw the full video centered; the canvas clips it to the 3:4 crop.
      ctx.drawImage(video, -vw / 2, -vh / 2, vw, vh);
      ctx.restore();
    } else {
      // Landscape sensor → 3:4 portrait center crop (e.g. 3840×2160 → 1620×2160),
      // mirrored to match the live preview.
      canvas.width = Math.round((vh * 3) / 4);
      canvas.height = vh;
      ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, -vw / 2, 0, vw, vh);
    }

    // Quality 0.88 keeps the full 4K resolution but roughly halves the upload
    // size vs 0.95 — the request was taking 2+ minutes with huge payloads.
    const shot = canvas.toDataURL("image/jpeg", 0.88);
    onCaptured(shot);
  }, [onCaptured]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      const t = setTimeout(doCapture, 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, doCapture]);

  const startCountdown = () => {
    if (!ready) return;
    setCountdown(3);
  };

  return (
    <div
      className="relative min-h-screen w-full flex flex-col px-4 sm:px-8 pt-3 pb-6"
      data-testid="capture-screen"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          disabled={countdown !== null}
          data-testid="capture-back-button"
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm tracking-[0.2em] uppercase disabled:opacity-30"
        >
          <ChevronLeft size={18} />
          Nazaj
        </button>
        <p className="text-[#A1A1AA] text-[10px] sm:text-xs tracking-[0.4em] uppercase">
          Korak 2 / 3
        </p>
        <div className="w-16" />
      </div>

      {/* Title */}
      <div className="text-center mb-3 fade-up">
        <h2
          className="font-display tracking-bond text-[#C9A961] text-2xl sm:text-3xl md:text-4xl uppercase"
          data-testid="capture-title"
        >
          Pripravi se, agent
        </h2>
      </div>

      {/* Webcam — sized so the shutter button below it lands around the middle
          of a portrait kiosk screen (reachable height) */}
      <div className="flex justify-center fade-up fade-up-d1">
        <div
          className="relative rounded-lg overflow-hidden border border-[#C9A961]/30 bg-black aspect-[3/4] max-w-full"
          // Portrait preview must fit the remaining viewport height, otherwise
          // the 3:4 frame overflows the screen and looks cropped.
          style={{ height: "min(52vh, 1100px)" }}
          data-testid="webcam-frame"
        >
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored={!cameraRotated}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.95}
            videoConstraints={videoConstraints}
            onUserMedia={() => setReady(true)}
            onUserMediaError={(e) =>
              setError(
                "Ni dostopa do kamere. Dovoli dostop v brskalniku.",
              )
            }
            className={cameraRotated ? "" : "w-full h-full object-cover"}
            style={cameraRotated ? rotatedVideoStyle : undefined}
          />

          {/* Crosshair overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* corners */}
            {[
              "top-4 left-4 border-t border-l",
              "top-4 right-4 border-t border-r",
              "bottom-4 left-4 border-b border-l",
              "bottom-4 right-4 border-b border-r",
            ].map((c, i) => (
              <div
                key={i}
                className={`absolute w-8 h-8 border-[#C9A961] crosshair-pulse ${c}`}
              />
            ))}
            {/* TARGET label */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] text-[#C9A961] uppercase">
              ● Cilj zaklenjen
            </div>
          </div>

          {/* Countdown */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span
                key={countdown}
                data-testid="countdown-number"
                className="font-display text-[#C9A961] text-[160px] leading-none letter-reveal"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.7)" }}
              >
                {countdown === 0 ? "•" : countdown}
              </span>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center">
              <p className="text-red-400 text-sm" data-testid="capture-error">
                {error}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-5 flex justify-center fade-up fade-up-d2">
        <button
          onClick={startCountdown}
          disabled={!ready || countdown !== null}
          data-testid="capture-shutter-button"
          className={`relative overflow-hidden border px-10 sm:px-16 py-5 sm:py-6 uppercase tracking-bond font-medium text-base sm:text-lg transition-all duration-300 inline-flex items-center gap-3 ${
            ready && countdown === null
              ? "border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 active:scale-[0.98]"
              : "border-white/10 text-white/30 cursor-not-allowed"
          }`}
        >
          <Camera size={20} />
          {countdown !== null ? "Slikam..." : "Slikaj me"}
        </button>
      </div>
    </div>
  );
}
